import './FilterPanel.scss';

import { IconClose } from '@hulu-react-style-components/icons';
import { SearchInput } from '@hulu/react-style-components';
import { addDays, addMonths, addWeeks, format } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { DropResult } from 'react-beautiful-dnd';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import type {
  DateOperatorFilter,
  FilterConfig,
  LineItemEndDateFilter,
  RangeFilterInput,
  RelativeDirection,
  RelativeUnit,
} from '../../../apis/graphql/types';
import {
  ComaprisonOperation,
  DateOperator,
  FilterOperator,
  FilterType,
  LineItemEndDateFilterUnit,
} from '../../../apis/graphql/types';
import Button from '../../../common/Button';
import Drawer from '../../../common/Drawer';
import { openToastAlert } from '../../../common/ToastAlert/toastAlert';
import { TRAFFICKING_SEARCH_PARAMS } from '../../../constants';
import type { GlobalFilters } from '../../../contexts/FilterContext';
import { useFiltersContext } from '../../../contexts/FilterContext';
import { isOperatorFilterValue, translateLegacyBrandOperator } from '../../../contexts/FilterContext/hooks/utils';
import bem from '../../../utils/bem';
import { isLineItemEndDateFilterValue } from '../TableFilterBar/AdditionalFilters/utils';
import {
  CustomSavedFiltersDropdown,
  DriftWarning,
  FilterNameRow,
  useIsFilterDrifted,
  VisibilityToggle,
} from './CustomSavedFilters';
import { useCreateCustomFilter } from './CustomSavedFilters/hooks/useCreateCustomFilter';
import { useUpdateCustomFilter } from './CustomSavedFilters/hooks/useUpdateCustomFilter';
import type { CustomFilterPayload, TraffickingCustomFilterSummary } from './CustomSavedFilters/types';
import { CustomFilterType, CustomFilterVisibility } from './CustomSavedFilters/types';
import { buildCustomFilterPayload, DEFAULT_NEW_FILTER_NAME } from './CustomSavedFilters/utils';
import FilterCategoryList from './FilterCategoryList/FilterCategoryList';
import type { FilterRowValue, MultiSelectState } from './FilterRow';
import FilterRow, { getSelectedOptions, isMultiSelectState } from './FilterRow';
import { todayLocalIso } from './FilterRow/DateControl/relativeDateRange';
import { isComparisonDateOperator } from './FilterRow/DateControl/types';
import { useCurrencyPrecisions } from './hooks/useCurrencyPrecisions';
import { useTraffickingFilterConfig } from './hooks/useTraffickingFilterConfig';
import { moneyInputDecimals, rangeInputNotice } from './moneyInputDecimals';
import { commitMultiSelectValue, isMultiSelectConfig, valuesToFilterOptions } from './multiSelectFilterUtils';
import { multiSelectToGlobalFilters } from './operatorQueryVariables';
import {
  makeDisplayConverter,
  makeSaveConverter,
  operatorValueToRangeFilter,
  rangeFilterToOperatorValue,
} from './rangeFilterUtils';

export interface FilterPanelProps {
  isOpen: boolean;
  onClose: () => void;
  scrollToFilterId?: string | null;
  onScrollComplete?: () => void;
}

const [block, element] = bem('filter-panel');

const NOOP_TOGGLE = (): void => {};

// Temporary bridge until V1 filter code is removed (see cleanup task).
// When clearing a V2 key, also clear its V1 counterpart so the TableFilterBar's
// `v2 ?? v1` fallback display doesn't show a stale V1 value.
const V2_TO_V1_ALIASES: Partial<Record<keyof GlobalFilters, keyof GlobalFilters>> = {
  traffickerEmailList: 'accountManager',
  accountExecutiveList: 'accountExecutive',
  adProductList: 'productType',
  // Zero Delivery: the config-driven panel writes the catalog boolean `hasZeroDeliveryRisk`, while
  // the legacy TableFilterBar toggle writes `pacingRiskList: ['ZERO_DELIVERY']`. Clearing the V2 key
  // in the panel also clears any lingering legacy value so a shareable link created before the flag
  // flip does not leave a stale pacingRiskList applied (KATANA-1513).
  hasZeroDeliveryRisk: 'pacingRiskList',
};

function getDraftFilterCount(value: FilterRowValue): number {
  if (value === null || value === undefined) return 0;
  if (isMultiSelectState(value)) return value.options.length;
  if (Array.isArray(value)) return value.length;
  // boolean
  if (typeof value === 'boolean') return 1;
  // date: { operator, date }
  if (typeof value === 'object' && 'date' in value && (value as { date?: string | null }).date) return 1;
  // range: { operator, value }
  if (
    typeof value === 'object' &&
    'operator' in value &&
    'value' in value &&
    ((value as { operator?: unknown }).operator !== null || (value as { value?: unknown }).value !== null)
  )
    return 1;
  return 0;
}

function hasAppliedValue(value: GlobalFilters[keyof GlobalFilters]): boolean {
  if (value === null || value === undefined) return false;
  if (Array.isArray(value)) return value.length > 0;
  // KATANA-1509: Brand's canonical value is {operator, values} — without this check an empty
  // `values` array (e.g. after clearing selections but before removing the row) would fall through
  // to the catch-all `return true` below and incorrectly count as applied.
  if (isOperatorFilterValue(value)) return value.values.length > 0;
  if (typeof value === 'object' && 'min' in value && 'max' in value) {
    const range = value as { min?: unknown; max?: unknown };
    return range.min != null || range.max != null;
  }
  return true;
}

// During the KATANA-1188 migration a shareable link may store lineItemEndDate in
// the legacy COMPARISON shape (comparisonOperation + duration + unit). The new
// CALENDAR panel only understands the absolute { operator, date } shape, so the
// relative duration is converted to an absolute date at hydration time. Because
// this runs each time the panel opens, the filter stays "relative to today".
// TODO: remove once a cleanup ticket is created and legacy links have expired.
function translateLegacyLineItemEndDate(value: LineItemEndDateFilter): FilterRowValue {
  const duration = value.duration ?? 0;
  const base = new Date();
  let target: Date;
  if (value.unit === LineItemEndDateFilterUnit.Month) {
    target = addMonths(base, duration);
  } else if (value.unit === LineItemEndDateFilterUnit.Week) {
    target = addWeeks(base, duration);
  } else {
    target = addDays(base, duration);
  }
  const operator =
    value.comparisonOperation === ComaprisonOperation.GreaterThan ? FilterOperator.IsAfter : FilterOperator.IsBefore;
  return { operator, date: format(target, 'yyyy-MM-dd') } as FilterRowValue;
}

function hydrateCalendarValue(
  config: FilterConfig,
  raw: GlobalFilters[keyof GlobalFilters]
): FilterRowValue | undefined {
  if (config.id === 'lineItemEndDate') {
    const value = raw as LineItemEndDateFilter | DateOperatorFilter | null | undefined;
    if (isLineItemEndDateFilterValue(value)) {
      return translateLegacyLineItemEndDate(value as LineItemEndDateFilter);
    }
  }
  // Re-anchor referenceDate to the loading user's local calendar day, overwriting what was
  // stored — a filter saved last week must still mean "next 7 days from today".
  const value = raw as DateOperatorFilter | null | undefined;
  if (value && value.operator === DateOperator.IsRelativeToToday) {
    return {
      operator: DateOperator.IsRelativeToToday,
      direction: value.direction as RelativeDirection,
      value: value.value ?? null,
      unit: value.unit as RelativeUnit,
      referenceDate: todayLocalIso(),
    } as FilterRowValue;
  }
  if (value && value.direction && isComparisonDateOperator(value.operator)) {
    return {
      operator: value.operator,
      direction: value.direction,
      value: value.value ?? null,
      unit: value.unit as RelativeUnit,
      referenceDate: todayLocalIso(),
    } as FilterRowValue;
  }
  return undefined;
}

function hydrateIncludeExcludeValue(
  config: FilterConfig,
  raw: GlobalFilters[keyof GlobalFilters],
  filters: GlobalFilters
): MultiSelectState {
  const { includeKey, excludeKey } = config.includeExclude!;
  // KATANA-1509: Brand's config.id ('brandIdOperator') differs from both includeKey/excludeKey
  // ('brandIdList'/'excludeBrandIdList') — prefer the canonical operator-object stored under
  // config.id when present, falling back to translating the legacy pair for records/links that
  // predate this migration (AC #5). Every other includeExclude config still has id === includeKey,
  // so `raw` is never an OperatorFilterValue for them and this branch is a no-op.
  if (isOperatorFilterValue(raw)) {
    return { operator: raw.operator, options: valuesToFilterOptions(config, raw.values) } as MultiSelectState;
  }
  const includeVal = filters[includeKey as keyof GlobalFilters];
  const excludeVal = filters[excludeKey as keyof GlobalFilters];
  const legacy = translateLegacyBrandOperator(includeVal, excludeVal);
  if (legacy) {
    return { operator: legacy.operator, options: valuesToFilterOptions(config, legacy.values) } as MultiSelectState;
  }
  const isExclude = !hasAppliedValue(includeVal) && hasAppliedValue(excludeVal);
  const operator = isExclude ? FilterOperator.DoesNotContain : FilterOperator.Contains;
  const source = isExclude ? excludeVal : includeVal;
  return { operator, options: valuesToFilterOptions(config, source) } as MultiSelectState;
}

function hydrateFilterValue(config: FilterConfig, filters: GlobalFilters): FilterRowValue {
  const raw = filters[config.id as keyof GlobalFilters];

  if (config.type === FilterType.Calendar) {
    return hydrateCalendarValue(config, raw) ?? (raw as FilterRowValue);
  }
  if (config.type === FilterType.Range) {
    if (typeof raw === 'number') {
      return { operator: FilterOperator.Equals, value: raw };
    }
    return rangeFilterToOperatorValue(
      raw as RangeFilterInput | null,
      makeDisplayConverter(config.rangeConfig?.displayMultiplier, config.valueDecimals)
    );
  }
  if (isMultiSelectConfig(config) && config.includeExclude) {
    return hydrateIncludeExcludeValue(config, raw, filters);
  }
  if (isMultiSelectConfig(config)) {
    return valuesToFilterOptions(config, raw);
  }
  return raw as FilterRowValue;
}

// KATANA-1509: Brand's config.id ('brandIdOperator') is distinct from both includeKey and
// excludeKey ('brandIdList'/'excludeBrandIdList') — every other includeExclude config has
// id === includeKey, so this returns [] for them (the id-based clear already covers includeKey,
// and the excludeKey-specific clears elsewhere in this file cover the rest). Centralizes "which
// legacy keys does this config leave behind" so every clear/remove path (staged-row detection,
// buildResolvedFilters, applyFiltersToContext, handleClear) agrees on the same list.
function legacyPairedKeysDistinctFromId(config: FilterConfig): string[] {
  const { includeExclude, id } = config;
  if (!includeExclude) return [];
  return [includeExclude.includeKey, includeExclude.excludeKey].filter((key) => key !== id);
}

type AddedFilter = { id: string; value: FilterRowValue };

interface FilterPanelContentProps {
  onClose: () => void;
  pendingNewFilterName: string | null;
  setPendingNewFilterName: (name: string | null) => void;
  isPendingNameEditing: boolean;
  setIsPendingNameEditing: (editing: boolean) => void;
  pendingVisibility: CustomFilterVisibility;
  setPendingVisibility: (visibility: CustomFilterVisibility) => void;
  scrollToFilterId?: string | null;
  onScrollComplete?: () => void;
}

const FilterPanelContent = ({
  onClose,
  pendingNewFilterName,
  setPendingNewFilterName,
  isPendingNameEditing,
  setIsPendingNameEditing,
  pendingVisibility,
  setPendingVisibility,
  scrollToFilterId,
  onScrollComplete,
}: FilterPanelContentProps): JSX.Element => {
  const showCustomFilters = true;
  const { filterConfigs, loading } = useTraffickingFilterConfig();
  const { filters, applyFilter, applyCustomFilter, selectedCustomFilter } = useFiltersContext();
  const isDrifted = useIsFilterDrifted();
  const [selectedFilterSummary, setSelectedFilterSummary] = useState<TraffickingCustomFilterSummary | null>(null);
  const handleFilterSummaryChange = useRef((summary: TraffickingCustomFilterSummary | null): void => {
    setSelectedFilterSummary(summary);
    setPendingNewFilterName(null);
  }).current;
  const [searchTerm, setSearchTerm] = useState('');
  const [addedFilters, setAddedFilters] = useState<AddedFilter[]>([]);
  const currencyPrecisions = useCurrencyPrecisions();

  const { createFilter, loading: creatingFilter } = useCreateCustomFilter();
  const { updateFilter, loading: updatingFilter } = useUpdateCustomFilter();

  const isOwnedFilter = selectedFilterSummary?.createdByMe === true;
  const isOwnedUserFilter = isOwnedFilter && selectedFilterSummary?.type === CustomFilterType.USER;

  const handleNameSaved = useCallback(
    (name: string) => {
      setSelectedFilterSummary((prev) => (prev ? { ...prev, name } : prev));
      if (selectedCustomFilter) {
        applyCustomFilter({ ...selectedCustomFilter, label: name });
      }
    },
    [selectedCustomFilter, applyCustomFilter]
  );

  const handleVisibilityChanged = useCallback((visibility: CustomFilterVisibility) => {
    setSelectedFilterSummary((prev) => (prev ? { ...prev, visibility } : prev));
  }, []);

  const handleSaveAsNew = useCallback(() => {
    setPendingNewFilterName(DEFAULT_NEW_FILTER_NAME);
    setIsPendingNameEditing(true);
    setSelectedFilterSummary(null);
  }, [setPendingNewFilterName, setIsPendingNameEditing]);

  const handlePendingNameChange = useCallback(
    (name: string) => {
      setPendingNewFilterName(name);
      setIsPendingNameEditing(false);
    },
    [setPendingNewFilterName, setIsPendingNameEditing]
  );

  const [baselineFilters, setBaselineFilters] = useState<AddedFilter[]>([]);

  // Hydrate the staged draft from the committed `filters` source of truth when
  // configs load or when a custom filter is selected/cleared (which updates filters in context).
  useEffect(() => {
    if (filterConfigs.length === 0) return;
    const staged: AddedFilter[] = filterConfigs
      .filter((c) => {
        if (hasAppliedValue(filters[c.id as keyof GlobalFilters])) return true;
        // Also show the row when only the excludeKey has a value (Is Not applied), or — for
        // Brand, whose includeKey/excludeKey are distinct from its config.id — when either legacy
        // paired key still has a value (pre-migration link/filter not yet load-boundary-translated).
        // legacyPairedKeysDistinctFromId already excludes whichever key equals c.id, so for
        // agency/industry-style configs (id === includeKey) this reduces to just excludeKey.
        return legacyPairedKeysDistinctFromId(c).some((key) => hasAppliedValue(filters[key as keyof GlobalFilters]));
      })
      .map((c) => ({ id: c.id, value: hydrateFilterValue(c, filters) }));

    // Stable-sort by saved displayOrder. Any staged id not in the saved order keeps
    // its config position at the end. Matching is case-insensitive as a safeguard
    // against payload storage normalising key casing.
    const savedOrder = filters.displayOrder;
    if (savedOrder && savedOrder.length > 0) {
      const orderMap = new Map(savedOrder.map((id, idx) => [id.toLowerCase(), idx]));
      staged.sort((a, b) => {
        const ai = orderMap.get(a.id.toLowerCase()) ?? Infinity;
        const bi = orderMap.get(b.id.toLowerCase()) ?? Infinity;
        return ai - bi;
      });
    }

    setAddedFilters(staged);
    setBaselineFilters(staged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterConfigs, selectedCustomFilter]);

  useEffect(() => {
    if (!scrollToFilterId || filterConfigs.length === 0) return;

    if (!addedFilters.some((f) => f.id === scrollToFilterId)) {
      handleAddFilter(scrollToFilterId);
    }

    const frameId = requestAnimationFrame(() => {
      const row = document.querySelector(`[data-testid="filter-row-${scrollToFilterId}"]`);
      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const focusTarget = row.querySelector<HTMLElement>(
          'input, select, button:not([aria-label^="remove"]):not([aria-label^="drag"])'
        );
        if (focusTarget) {
          setTimeout(() => focusTarget.focus(), 300);
        }
      }
      onScrollComplete?.();
    });

    return () => cancelAnimationFrame(frameId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollToFilterId, filterConfigs]);

  const isStagedDrifted = useMemo(() => {
    if (!selectedCustomFilter) return false;
    if (addedFilters.length !== baselineFilters.length) return true;
    const baselineMap = new Map(baselineFilters.map((f) => [f.id, f.value]));
    const valuesDrifted = addedFilters.some(
      (f) => !baselineMap.has(f.id) || JSON.stringify(f.value) !== JSON.stringify(baselineMap.get(f.id))
    );
    if (valuesDrifted) return true;
    // Also treat reordering as drift so "Save Changes" activates after dragging.
    return addedFilters.some((f, i) => f.id !== baselineFilters[i].id);
  }, [selectedCustomFilter, addedFilters, baselineFilters]);

  const selectedCounts = useMemo(() => {
    const draftById = new Map(addedFilters.map((f) => [f.id, f.value]));
    return Object.fromEntries(
      filterConfigs.map((c) => {
        const draftValue = draftById.get(c.id);
        return [c.id, draftById.has(c.id) ? getDraftFilterCount(draftValue ?? null) : 0];
      })
    );
  }, [addedFilters, filterConfigs]);

  const filteredConfigs = searchTerm
    ? filterConfigs.filter((c) => c.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : filterConfigs;

  const addedFilterIds = useMemo(() => new Set(addedFilters.map((f) => f.id)), [addedFilters]);

  const selectedCurrencies = useMemo(
    () =>
      getSelectedOptions(addedFilters.find((f) => f.id === TRAFFICKING_SEARCH_PARAMS.CURRENCY_LIST)?.value ?? null).map(
        (option) => option.value
      ),
    [addedFilters]
  );

  const configById = useMemo(() => {
    const map = new Map<string, typeof filterConfigs[number]>();
    for (const c of filterConfigs) map.set(c.id, c);
    return map;
  }, [filterConfigs]);

  const handleAddFilter = useCallback(
    (id: string) => {
      setAddedFilters((prev) => {
        if (prev.some((f) => f.id === id)) return prev;
        const config = configById.get(id);
        let defaultValue: FilterRowValue = null;
        if (config?.isBoolean) {
          defaultValue = true;
        } else if (config?.isSearchable) {
          defaultValue = [];
        } else if (config?.type === FilterType.Range) {
          defaultValue = { operator: null, value: null };
        } else if (config?.type === FilterType.Calendar) {
          const firstOperator = config.operators?.[0] ?? FilterOperator.Is;
          defaultValue = { operator: firstOperator as 'IS', date: format(new Date(), 'yyyy-MM-dd') };
        } else if (config) {
          // Catch-all for the non-searchable multi-select control (CHECKBOX/
          // CHECKBOXES/DROPDOWN/RADIOBUTTON enum lists): start with no selections.
          defaultValue = [];
        }
        return [...prev, { id, value: defaultValue }];
      });
    },
    [configById]
  );

  const handleRemoveFilter = useCallback((id: string) => {
    setAddedFilters((prev) => prev.filter((f) => f.id !== id));
  }, []);

  const handleChangeFilter = useCallback((id: string, value: FilterRowValue) => {
    setAddedFilters((prev) => prev.map((f) => (f.id === id ? { ...f, value } : f)));
  }, []);

  const handleDragEnd = useCallback(({ source, destination }: DropResult): void => {
    if (!destination || source.index === destination.index) return;
    setAddedFilters((prev) => {
      const reordered = [...prev];
      const [moved] = reordered.splice(source.index, 1);
      reordered.splice(destination.index, 0, moved);
      return reordered;
    });
  }, []);

  const resolveFilterValue = useCallback(
    (f: AddedFilter): Record<string, unknown> => {
      const config = configById.get(f.id);

      if (isMultiSelectState(f.value) && config?.includeExclude) {
        const mutations = multiSelectToGlobalFilters(config, f.value);
        return mutations;
      }

      if (
        config?.type === FilterType.Range &&
        f.value !== null &&
        typeof f.value === 'object' &&
        !Array.isArray(f.value) &&
        'operator' in f.value &&
        'value' in f.value
      ) {
        return {
          [f.id]: operatorValueToRangeFilter(f.value, makeSaveConverter(config.rangeConfig?.displayMultiplier)),
        };
      }

      if (config && isMultiSelectConfig(config) && isMultiSelectState(f.value)) {
        return { [f.id]: commitMultiSelectValue(config, f.value.options) };
      }

      if (config && isMultiSelectConfig(config) && Array.isArray(f.value)) {
        return { [f.id]: commitMultiSelectValue(config, f.value) };
      }

      return { [f.id]: f.value };
    },
    [configById]
  );

  const buildResolvedFilters = useCallback((): GlobalFilters => {
    const resolved = { ...filters } as Record<string, unknown>;
    addedFilters.forEach((f) => {
      const mutations = resolveFilterValue(f);
      Object.assign(resolved, mutations);
    });
    filterConfigs.forEach((c) => {
      if (!addedFilterIds.has(c.id)) {
        if (hasAppliedValue(filters[c.id as keyof GlobalFilters])) {
          resolved[c.id] = null;
        }
        // Clears excludeKey for every includeExclude config, and — for Brand, whose
        // includeKey/excludeKey are distinct from its config.id — includeKey too.
        legacyPairedKeysDistinctFromId(c).forEach((key) => {
          if (hasAppliedValue(filters[key as keyof GlobalFilters])) {
            resolved[key] = null;
          }
        });
      }
    });
    return resolved as GlobalFilters;
  }, [addedFilterIds, addedFilters, resolveFilterValue, filterConfigs, filters]);

  const applyFiltersToContext = useCallback(() => {
    addedFilters.forEach((f) => {
      const mutations = resolveFilterValue(f);
      Object.entries(mutations).forEach(([key, val]) => {
        applyFilter(key as keyof GlobalFilters, val as GlobalFilters[keyof GlobalFilters]);
      });
    });
    filterConfigs.forEach((c) => {
      if (!addedFilterIds.has(c.id)) {
        if (hasAppliedValue(filters[c.id as keyof GlobalFilters])) {
          applyFilter(c.id as keyof GlobalFilters, null as GlobalFilters[keyof GlobalFilters]);
        }
        const v1Key = V2_TO_V1_ALIASES[c.id as keyof GlobalFilters];
        if (v1Key && hasAppliedValue(filters[v1Key])) {
          applyFilter(v1Key, null as GlobalFilters[keyof GlobalFilters]);
        }
        // Clears excludeKey for every includeExclude config, and — for Brand, whose
        // includeKey/excludeKey are distinct from its config.id — includeKey too.
        legacyPairedKeysDistinctFromId(c).forEach((key) => {
          const legacyKey = key as keyof GlobalFilters;
          if (hasAppliedValue(filters[legacyKey])) {
            applyFilter(legacyKey, null as GlobalFilters[keyof GlobalFilters]);
          }
        });
      }
    });
    // Persist drag-reorder within the session. Only populated rows are stored so
    // the array aligns with what will re-stage on the next panel open.
    // Guard: only write if there is something to store or a previous order to clear.
    const orderedPopulatedIds = addedFilters.filter((f) => getDraftFilterCount(f.value) > 0).map((f) => f.id);
    if (orderedPopulatedIds.length > 0 || (filters.displayOrder && filters.displayOrder.length > 0)) {
      applyFilter('displayOrder', orderedPopulatedIds as GlobalFilters[keyof GlobalFilters]);
    }
  }, [addedFilterIds, addedFilters, resolveFilterValue, applyFilter, filterConfigs, filters]);

  const handleSaveChanges = useCallback(async () => {
    if (!selectedFilterSummary) return;
    applyFiltersToContext();
    try {
      const resolved = buildResolvedFilters();
      const orderedPopulatedIds = addedFilters.filter((f) => getDraftFilterCount(f.value) > 0).map((f) => f.id);
      const payload: CustomFilterPayload = {
        ...buildCustomFilterPayload(resolved, filterConfigs),
        displayOrder: orderedPopulatedIds,
      };
      await updateFilter(selectedFilterSummary.id, { filterPayload: payload });
      setBaselineFilters(addedFilters);
      if (selectedCustomFilter) {
        applyCustomFilter({
          ...selectedCustomFilter,
          value: (payload as unknown) as typeof selectedCustomFilter.value,
        });
      }
    } catch (e) {
      openToastAlert({
        alertType: 'error',
        message: e instanceof Error ? e.message : 'Failed to save changes',
      });
    }
  }, [
    selectedFilterSummary,
    applyFiltersToContext,
    buildResolvedFilters,
    addedFilters,
    updateFilter,
    selectedCustomFilter,
    applyCustomFilter,
    filterConfigs,
  ]);

  const handleApply = useCallback(() => {
    applyFiltersToContext();
    onClose();
  }, [applyFiltersToContext, onClose]);

  const handleSaveNewFilter = useCallback(async () => {
    if (!pendingNewFilterName) return;
    applyFiltersToContext();
    try {
      const orderedPopulatedIds = addedFilters.filter((f) => getDraftFilterCount(f.value) > 0).map((f) => f.id);
      const payload: CustomFilterPayload = {
        ...buildCustomFilterPayload(buildResolvedFilters(), filterConfigs),
        displayOrder: orderedPopulatedIds,
      };
      const created = await createFilter(pendingNewFilterName, payload, pendingVisibility);
      applyCustomFilter({
        id: created.id,
        label: created.name,
        value: created.payload,
      });
      setPendingNewFilterName(null);
      setPendingVisibility(CustomFilterVisibility.PRIVATE);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to save custom filter';
      const isDuplicate =
        message.toLowerCase().includes('duplicate') ||
        message.toLowerCase().includes('already exists') ||
        message.includes('Cannot return null');
      openToastAlert({
        alertType: 'error',
        message: isDuplicate ? 'A filter with this name already exists. Please choose a different name.' : message,
      });
    }
  }, [
    applyFiltersToContext,
    buildResolvedFilters,
    pendingNewFilterName,
    pendingVisibility,
    createFilter,
    applyCustomFilter,
    setPendingNewFilterName,
    setPendingVisibility,
    addedFilters,
    filterConfigs,
  ]);

  const handleClear = useCallback(() => {
    addedFilters.forEach((f) => {
      if (hasAppliedValue(filters[f.id as keyof GlobalFilters])) {
        applyFilter(f.id as keyof GlobalFilters, null as GlobalFilters[keyof GlobalFilters]);
      }
      // Clears Brand's legacy brandIdList/excludeBrandIdList pair too — its config.id
      // ('brandIdOperator') is distinct from both, so the f.id clear above doesn't reach them
      // (KATANA-1509).
      const config = configById.get(f.id);
      if (config) {
        legacyPairedKeysDistinctFromId(config).forEach((key) => {
          const legacyKey = key as keyof GlobalFilters;
          if (hasAppliedValue(filters[legacyKey])) {
            applyFilter(legacyKey, null as GlobalFilters[keyof GlobalFilters]);
          }
        });
      }
    });
    // Clear V1 aliases off the alias map itself, not the staged rows: a legacy shareable link can
    // carry an applied alias value (e.g. pacingRiskList) with no staged panel row for its V2 key, so
    // driving this off addedFilters would leave the hidden filter active after Clear (KATANA-1513).
    (Object.values(V2_TO_V1_ALIASES) as Array<keyof GlobalFilters>).forEach((v1Key) => {
      if (hasAppliedValue(filters[v1Key])) {
        applyFilter(v1Key, null as GlobalFilters[keyof GlobalFilters]);
      }
    });
    // Only reset displayOrder if it was previously committed — avoids a spurious
    // applyFilter call when the user clicks Clear with no applied filters.
    if (filters.displayOrder && filters.displayOrder.length > 0) {
      applyFilter('displayOrder', [] as GlobalFilters[keyof GlobalFilters]);
    }
    setAddedFilters([]);
    if (selectedCustomFilter) {
      applyCustomFilter(null);
    }
    setSelectedFilterSummary(null);
    setPendingNewFilterName(null);
    setPendingVisibility(CustomFilterVisibility.PRIVATE);
  }, [
    addedFilters,
    applyFilter,
    filters,
    selectedCustomFilter,
    applyCustomFilter,
    configById,
    setPendingNewFilterName,
    setPendingVisibility,
  ]);

  return (
    <div className={block()} data-testid="filters-modal-container">
      <header className={element('header')}>
        <h2 className={element('title')}>Filters</h2>
        <button className={element('close-btn')} onClick={onClose} aria-label="close filter panel">
          <IconClose width="20px" height="20px" title="Close" />
        </button>
      </header>
      <div className={element('body')}>
        <div className={element('left-panel')}>
          <div className={element('search-filters')}>
            <SearchInput
              placeholder="Search filters..."
              onInput={(value: string): void => setSearchTerm(value)}
              onClear={(): void => setSearchTerm('')}
              value={searchTerm}
            />
          </div>
          {showCustomFilters && !searchTerm && (
            <div className={element('custom-filters')}>
              <h3 className={element('custom-filters-title')}>Custom Filters</h3>
              <p className={element('custom-filters-description')}>Access and reuse custom filter combinations here.</p>
              <CustomSavedFiltersDropdown variant="panel" onFilterSummaryChange={handleFilterSummaryChange} />
            </div>
          )}
          <div className={element('select-filters', searchTerm ? 'searching' : undefined)}>
            {!searchTerm && <h3 className={element('select-filters-title')}>Select Filters</h3>}
            {loading ? (
              <div className={element('loading')}>Loading filters…</div>
            ) : (
              <FilterCategoryList
                configs={filteredConfigs}
                isFiltered={!!searchTerm}
                selectedCounts={selectedCounts}
                addedFilterIds={addedFilterIds}
                onAddFilter={handleAddFilter}
                expandForFilterId={scrollToFilterId}
              />
            )}
          </div>
        </div>
        <div className={element('right-panel')}>
          {showCustomFilters && pendingNewFilterName !== null && (
            <>
              <div className={element('custom-filter-header')}>
                <FilterNameRow
                  filterId="pending-new"
                  filterName={pendingNewFilterName}
                  onNameSaved={handlePendingNameChange}
                  readOnly={false}
                  initialEditing={isPendingNameEditing}
                  skipMutation
                />
                <VisibilityToggle
                  filterId="pending-new"
                  visibility={pendingVisibility}
                  onVisibilityChanged={setPendingVisibility}
                  skipMutation
                />
              </div>
              <DriftWarning message="This filter has not been saved yet. Click Save Changes to save." />
            </>
          )}
          {showCustomFilters && !pendingNewFilterName && selectedFilterSummary && (
            <>
              <div className={element('custom-filter-header')}>
                <FilterNameRow
                  filterId={selectedFilterSummary.id}
                  filterName={selectedFilterSummary.name}
                  filterPayload={selectedCustomFilter?.value}
                  onNameSaved={handleNameSaved}
                  onDeleted={handleClear}
                  readOnly={!isOwnedUserFilter}
                  initialEditing={false}
                  visibility={selectedFilterSummary.visibility}
                />
                <VisibilityToggle
                  filterId={selectedFilterSummary.id}
                  visibility={selectedFilterSummary.visibility}
                  filterPayload={selectedCustomFilter?.value}
                  onVisibilityChanged={handleVisibilityChanged}
                  disabled={!isOwnedUserFilter}
                />
              </div>
              {(isDrifted || isStagedDrifted) && <DriftWarning />}
            </>
          )}
          {addedFilters.length > 0 && (
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="filter-dimensions">
                {(provided): JSX.Element => (
                  <div className={element('filters-container')} ref={provided.innerRef} {...provided.droppableProps}>
                    {addedFilters.map((f, index) => {
                      const config = configById.get(f.id);
                      if (!config) return null;
                      const inputDecimals = moneyInputDecimals(config, currencyPrecisions, selectedCurrencies);
                      return (
                        <Draggable key={f.id} draggableId={f.id} index={index}>
                          {(draggableProvided, snapshot): JSX.Element => (
                            <FilterRow
                              config={config}
                              value={f.value}
                              onChange={handleChangeFilter}
                              onRemove={handleRemoveFilter}
                              rangeDecimals={inputDecimals}
                              rangeNotice={rangeInputNotice(config, inputDecimals, f.value)}
                              innerRef={draggableProvided.innerRef}
                              draggableProps={draggableProvided.draggableProps}
                              dragHandleProps={draggableProvided.dragHandleProps}
                              isDragging={snapshot.isDragging}
                            />
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          )}
        </div>
      </div>
      <footer className={element('footer')}>
        <Button className={element('clear-btn')} onClick={handleClear}>
          Clear
        </Button>
        {showCustomFilters && (isDrifted || isStagedDrifted) && isOwnedUserFilter && selectedFilterSummary && (
          <Button onClick={handleSaveChanges} disabled={updatingFilter} data-testid="overwrite-custom-filter-btn">
            {updatingFilter ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
        {showCustomFilters && pendingNewFilterName && (
          <Button onClick={handleSaveNewFilter} disabled={creatingFilter} data-testid="save-new-custom-filter-btn">
            {creatingFilter ? 'Saving...' : 'Save Changes'}
          </Button>
        )}
        {showCustomFilters && (
          <Button onClick={handleSaveAsNew} data-testid="save-as-custom-filter-btn">
            Save as Custom Filter
          </Button>
        )}
        <Button className={element('apply-btn')} onClick={handleApply}>
          Apply
        </Button>
      </footer>
    </div>
  );
};

const FilterPanel = ({ isOpen, onClose, scrollToFilterId, onScrollComplete }: FilterPanelProps): JSX.Element => {
  const [pendingNewFilterName, setPendingNewFilterName] = useState<string | null>(null);
  const [isPendingNameEditing, setIsPendingNameEditing] = useState(false);
  const [pendingVisibility, setPendingVisibility] = useState<CustomFilterVisibility>(CustomFilterVisibility.PRIVATE);

  const renderContent = useCallback(
    () => (
      <FilterPanelContent
        onClose={onClose}
        pendingNewFilterName={pendingNewFilterName}
        setPendingNewFilterName={setPendingNewFilterName}
        isPendingNameEditing={isPendingNameEditing}
        setIsPendingNameEditing={setIsPendingNameEditing}
        pendingVisibility={pendingVisibility}
        setPendingVisibility={setPendingVisibility}
        scrollToFilterId={scrollToFilterId}
        onScrollComplete={onScrollComplete}
      />
    ),
    [onClose, pendingNewFilterName, isPendingNameEditing, pendingVisibility, scrollToFilterId, onScrollComplete]
  );

  return (
    <Drawer
      className="filter-panel"
      isOpen={isOpen}
      onClose={onClose}
      isExpanded={false}
      onToggleExpansion={NOOP_TOGGLE}
      emptyHeader
      openFrom="left"
      size="large"
      closeOnOutsideClick
      content={renderContent}
    />
  );
};

export default FilterPanel;
